import React, { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { IDocumentShareAttachment, IDocumentShareView } from '../../models/DocumentShare';
import {
    buildDocumentSharePdf,
    downloadPdfBytes,
    pdfFileName,
    pdfPageCount,
} from './documentSharePdf';
import { useDocumentShareStyles, DOC_ICON_FG } from './documentShareStyles';

interface DocumentExportPanelProps {
    view: IDocumentShareView;
    attachments: IDocumentShareAttachment[];
    /** Resolves a token-scoped download URL for an attachment id. */
    urlFor: (attachmentId: number) => string;
    showDownload?: boolean;
}

function formatSize(bytes: number): string {
    if (!bytes || bytes <= 0) return '';
    const kb = bytes / 1024;
    return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Shows the shared document's attachments merged into a single PDF, previewed via a
 * same-origin blob URL in the browser's built-in viewer, with a Download button.
 * Any branding/cover page is applied server-side, so this just frames the document
 * as the backend serves it — no client-side branding.
 */
const DocumentExportPanel: React.FC<DocumentExportPanelProps> = ({
    view,
    attachments,
    urlFor,
    showDownload = true,
}) => {
    const classes = useDocumentShareStyles();
    const [bytes, setBytes] = useState<Uint8Array | null>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [pageCount, setPageCount] = useState(0);
    const [failed, setFailed] = useState(false);

    // urlFor is a fresh closure each render; hold it in a ref so rebuilding keys off the
    // document, not the callback identity.
    const urlForRef = useRef(urlFor);
    urlForRef.current = urlFor;

    useEffect(() => {
        let active = true;
        let created: string | null = null;
        setBytes(null);
        setBlobUrl(null);
        setPageCount(0);
        setFailed(false);
        buildDocumentSharePdf(view, urlForRef.current)
            .then(async (out) => {
                if (!active) return;
                const pages = await pdfPageCount(out);
                if (!active) return;
                setBytes(out);
                setPageCount(pages);
                created = URL.createObjectURL(new Blob([out], { type: 'application/pdf' }));
                setBlobUrl(created);
            })
            .catch(() => {
                if (active) setFailed(true);
            });
        return () => {
            active = false;
            if (created) URL.revokeObjectURL(created);
        };
    }, [view]);

    const primary = attachments[0];
    const fileLabel = primary?.fileName ?? `${view.documentName || 'document'}.pdf`;
    const sizeStr = bytes ? formatSize(bytes.length) : formatSize(primary?.fileSize ?? 0);
    const meta = ['PDF', pageCount ? `${pageCount} page${pageCount === 1 ? '' : 's'}` : '', sizeStr]
        .filter(Boolean)
        .join(' · ');

    return (
        <div>
            {showDownload && (
                <div className={classes.exportToolbar}>
                    <div className={classes.exportActions}>
                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={<GetAppIcon />}
                            className={classes.greenButton}
                            disabled={!bytes}
                            onClick={() => bytes && downloadPdfBytes(bytes, pdfFileName(view))}
                            data-testid="document-share-export-download"
                        >
                            Download
                        </Button>
                    </div>
                </div>
            )}

            <div className={classes.fileCard}>
                <div className={classes.fileCardIcon}>
                    <DescriptionOutlinedIcon style={{ color: DOC_ICON_FG }} />
                </div>
                <div className={classes.fileCardBody}>
                    <Typography className={classes.fileCardName} noWrap>
                        {fileLabel}
                    </Typography>
                    <Typography className={classes.fileCardMeta}>{meta}</Typography>
                </div>
            </div>

            <div className={classes.previewWrap}>
                {failed ? (
                    <Typography className={classes.emptyHint} style={{ padding: 16 }}>
                        Unable to generate the preview.
                    </Typography>
                ) : !blobUrl ? (
                    <div className={classes.brandedPreviewLoading}>
                        <CircularProgress size={22} />
                        <Typography variant="body2" style={{ marginTop: 12 }}>
                            Preparing PDF…
                        </Typography>
                    </div>
                ) : (
                    <iframe
                        src={`${blobUrl}#navpanes=0&view=FitH`}
                        title="Document preview"
                        className={classes.previewFrame}
                    />
                )}
            </div>
        </div>
    );
};

export default DocumentExportPanel;
